import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Globe, Check } from "lucide-react";

/**
 * Premium language switcher. Mobile-responsive.
 * - Pill style on desktop showing globe + short code (EN / हिं / मरा)
 * - Dropdown menu with full language names
 * - data-testid="language-switcher"
 */
export default function LanguageSwitcher({ variant = "light", className = "" }) {
  const { lang, setLang, languages } = useI18n();
  const [open, setOpen] = useState(false);

  const current = languages.find((l) => l.code === lang) || languages[0];

  const isDark = variant === "dark";
  const baseClasses = isDark
    ? "bg-white/10 text-white hover:bg-white/20 border-white/20"
    : "bg-white/70 text-amethyst-800 hover:bg-white border-amethyst-200/60";

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur border transition-all ${baseClasses}`}
        data-testid="language-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
        <span className="hidden sm:inline whitespace-nowrap">{current.label}</span>
        <span className="sm:hidden whitespace-nowrap">{current.short}</span>
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-2 w-44 rounded-2xl float-card overflow-hidden z-50 py-1.5"
          role="listbox"
          data-testid="language-menu"
        >
          {languages.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setLang(l.code); setOpen(false); }}
                className={`flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-magenta-50 transition-colors ${l.code === lang ? "text-magenta-700 font-semibold" : "text-amethyst-800"}`}
                data-testid={`lang-option-${l.code}`}
                aria-selected={l.code === lang}
              >
                <span>{l.label}</span>
                {l.code === lang && <Check className="w-3.5 h-3.5 text-magenta-600" strokeWidth={2.5} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
